import type { Category, CustomFieldDefinition, Item, ItemGroupPrice, ItemStockSummary } from '@erp/shared';
import { Check, ChevronRight, Info, Layers, Tags, Warehouse as WarehouseIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge, Card } from '../../../components/shadcn/index.js';
import { Button } from '../../../components/ui/index.js';
import { formatDateTime } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';
import { isHtmlEmpty, sanitizeHtml } from '../../../lib/sanitizeHtml.js';

import { fmtQty } from './profileFormat.js';
import { PackHint, QtyText, SectionTitle } from './ProfileParts.js';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="mt-0.5 text-sm text-fg">{children}</div>
    </div>
  );
}

function Flag({ on, label }: { on: boolean; label: string }): JSX.Element {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${on ? 'border-success text-success' : 'border-border text-fg-subtle'}`}>
      {on ? <Check className="h-3 w-3" aria-hidden /> : <X className="h-3 w-3" aria-hidden />}
      {label}
    </span>
  );
}

function cfDisplay(def: CustomFieldDefinition, raw: unknown, yes: string, no: string): React.ReactNode {
  if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
    return <span className="text-fg-subtle">—</span>;
  }
  if (def.fieldType === 'boolean') return raw ? yes : no;
  if (def.fieldType === 'multiselect' && Array.isArray(raw)) {
    return (
      <span className="flex flex-wrap gap-1">
        {raw.map((v) => <Badge key={String(v)} variant="secondary">{String(v)}</Badge>)}
      </span>
    );
  }
  return String(raw);
}

/** Root › … › leaf for a category id. Guarded against a cycle in the tree. */
export function categoryPath(categories: Category[], id: string | null): string[] {
  if (!id) return [];
  const byId = new Map(categories.map((c) => [c.id, c]));
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur.name);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return out;
}

interface Props {
  item: Item;
  categories: Category[];
  brandName: string | null;
  defs: CustomFieldDefinition[];
  groupPrices: ItemGroupPrice[];
  variantsEnabled: boolean;
  multiCategory: boolean;
  summary: ItemStockSummary | null;
  pack: { name: string; factor: number } | null;
  canStock: boolean;
  onOpenStock: () => void;
  onOpenPrices: () => void;
}

/** The product at a glance: what it is, how it is sold, where it is, what it costs to buy into. */
export function ItemOverviewTab({
  item, categories, brandName, defs, groupPrices, variantsEnabled, multiCategory, summary, pack, canStock, onOpenStock, onOpenPrices,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const locale = i18n.language;
  const yes = t('list.yes', { defaultValue: 'Yes' });
  const no = t('list.no', { defaultValue: 'No' });
  const money = (v?: string | null, c?: string | null): string => (v && c ? formatMoney(v, { locale, currency: c }) : '—');
  const cleanDesc = sanitizeHtml(item.description ?? '');
  const path = categoryPath(categories, item.categoryId);
  const extra = multiCategory ? (item.extraCategoryIds ?? []).map((id) => categoryPath(categories, id).join(' › ')).filter(Boolean) : [];
  const baseGroupPrices = groupPrices.filter((g) => g.itemUnitId === null && g.unitPrice != null);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="flex flex-col gap-4 lg:col-span-2">
        <Card className="p-6">
          <SectionTitle icon={Info}>{t('profile.overview.details', { defaultValue: 'Details' })}</SectionTitle>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            <Field label={t('create.type')}>{t(`type.${item.itemType}`)}</Field>
            <Field label={t('create.category', { defaultValue: 'Category' })}>
              {path.length === 0 ? '—' : (
                <span className="flex flex-wrap items-center gap-0.5">
                  {path.map((p, i) => (
                    <span key={`${p}-${i}`} className="inline-flex items-center gap-0.5">
                      {i > 0 && <ChevronRight className="h-3 w-3 text-fg-subtle rtl:scale-x-[-1]" aria-hidden />}
                      <span className={i === path.length - 1 ? 'font-medium' : 'text-fg-muted'}>{p}</span>
                    </span>
                  ))}
                </span>
              )}
            </Field>
            <Field label={t('create.brand', { defaultValue: 'Brand' })}>{brandName ?? '—'}</Field>
            <Field label={t('profile.overview.baseUnit', { defaultValue: 'Base unit' })}>{item.unitOfMeasure}</Field>
            <Field label={t('profile.overview.packs', { defaultValue: 'Packs' })}>
              {item.units.length === 0 ? '—' : (
                <span className="flex flex-wrap gap-1">
                  {item.units.map((u) => (
                    <Badge key={u.id} variant={u.isActive ? 'secondary' : 'warning'}>
                      {u.name} <span dir="ltr" className="font-mono">×{fmtQty(u.factor, locale)}</span>
                    </Badge>
                  ))}
                </span>
              )}
            </Field>
            <Field label={t('create.taxRate', { defaultValue: 'Tax rate' })}>{item.taxRate != null ? `${fmtQty(item.taxRate, locale)}%` : '—'}</Field>
            <Field label={t('create.barcode')}>{item.barcode ? <span dir="ltr" className="font-mono text-xs">{item.barcode}</span> : '—'}</Field>
            <Field label={t('profile.overview.created', { defaultValue: 'Created' })}>{formatDateTime(item.createdAt, { locale })}</Field>
            <Field label={t('profile.overview.updated', { defaultValue: 'Last changed' })}>{formatDateTime(item.updatedAt, { locale })}</Field>
            {extra.length > 0 && (
              <div className="col-span-2 md:col-span-3">
                <Field label={t('profile.overview.alsoIn', { defaultValue: 'Also listed under' })}>
                  <span className="flex flex-wrap gap-1">{extra.map((e) => <Badge key={e} variant="outline">{e}</Badge>)}</span>
                </Field>
              </div>
            )}
            {item.videoUrl && (
              <div className="col-span-2 md:col-span-3">
                <Field label={t('profile.overview.video', { defaultValue: 'Video' })}>
                  <a href={item.videoUrl} target="_blank" rel="noreferrer" className="break-all text-brand hover:underline">{item.videoUrl}</a>
                </Field>
              </div>
            )}
          </div>
          <div className="mt-5 flex flex-wrap gap-1.5 border-t border-border pt-4">
            <Flag on={item.isSellable} label={t('profile.flags.sellable', { defaultValue: 'Sold' })} />
            <Flag on={item.isPurchasable} label={t('profile.flags.purchasable', { defaultValue: 'Bought' })} />
            <Flag on={item.isInventoryTracked} label={t('profile.flags.tracked', { defaultValue: 'Stock tracked' })} />
            <Flag on={item.earnsCashback} label={t('profile.flags.cashback', { defaultValue: 'Earns cashback' })} />
            <Flag on={item.isPublished} label={t('profile.flags.published', { defaultValue: 'On the website' })} />
            <Flag on={!item.isHidden} label={t('profile.flags.inApp', { defaultValue: 'Shown in the app' })} />
            {item.isCombo && <Flag on label={t('profile.flags.combo', { defaultValue: 'Combo' })} />}
            {item.isRawMaterial && <Flag on label={t('profile.flags.raw', { defaultValue: 'Raw material' })} />}
          </div>
        </Card>

        <Card className="p-6">
          <SectionTitle>{t('form.sections.description', { defaultValue: 'Description' })}</SectionTitle>
          {isHtmlEmpty(cleanDesc)
            ? <p className="text-sm text-fg-subtle">{t('detail.noDescription', { defaultValue: 'No description.' })}</p>
            : <div className="rte-content" dangerouslySetInnerHTML={{ __html: cleanDesc }} />}
        </Card>

        {defs.length > 0 && (
          <Card className="p-6">
            <SectionTitle>{t('create.additionalDetails', { defaultValue: 'Additional details' })}</SectionTitle>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {defs.map((d) => <Field key={d.id} label={d.label}>{cfDisplay(d, item.customFields?.[d.code], yes, no)}</Field>)}
            </div>
          </Card>
        )}

        {variantsEnabled && item.variants.length > 0 && (
          <Card className="p-6">
            <SectionTitle icon={Layers}>{t('form.sections.variants', { defaultValue: 'Variants' })}</SectionTitle>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    <th className="py-2 pe-3 text-start">{t('form.variants.name', { defaultValue: 'Variant' })}</th>
                    <th className="py-2 pe-3 text-end">{t('create.salePrice')}</th>
                    <th className="py-2 pe-3 text-end">{t('form.variants.stock', { defaultValue: 'Stock' })}</th>
                    <th className="py-2 text-start">{t('create.barcode')}</th>
                  </tr>
                </thead>
                <tbody>
                  {item.variants.map((v) => (
                    <tr key={v.id} className="border-b border-border last:border-0">
                      <td className="py-2 pe-3 font-medium">
                        {v.name}
                        {v.nameAr ? <span className="block text-xs font-normal text-fg-muted" dir="rtl">{v.nameAr}</span> : null}
                      </td>
                      <td className="py-2 pe-3 text-end font-mono tabular-nums">{money(v.salePrice ?? item.salePrice, item.saleCurrencyCode)}</td>
                      <td className="py-2 pe-3 text-end">{v.stockQty != null ? <QtyText value={v.stockQty} /> : '—'}</td>
                      <td className="py-2">{v.barcode ? <span dir="ltr" className="font-mono text-xs">{v.barcode}</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {canStock && item.itemType !== 'service' && summary && (
          <Card className="p-5">
            <SectionTitle
              icon={WarehouseIcon}
              end={<Button variant="ghost" size="sm" onClick={onOpenStock}>{t('profile.overview.openStockCard', { defaultValue: 'Stock card' })}</Button>}
            >
              {t('profile.overview.whereItIs', { defaultValue: 'Where it is' })}
            </SectionTitle>
            <ul className="flex flex-col divide-y divide-border">
              {summary.warehouses.map((w) => (
                <li key={w.warehouseId} className="flex items-start justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{w.name}</span>
                    <span className="font-mono text-[11px] text-fg-subtle">{w.code}</span>
                  </span>
                  <span className="flex flex-col items-end">
                    <QtyText value={w.quantity} unit={item.unitOfMeasure} strong />
                    <PackHint quantity={w.quantity} pack={pack} baseUnit={item.unitOfMeasure} />
                  </span>
                </li>
              ))}
              <li className="flex items-start justify-between gap-3 pt-2.5">
                <span className="text-sm font-bold">{t('profile.stock.total', { defaultValue: 'Total' })}</span>
                <span className="flex flex-col items-end">
                  <QtyText value={summary.totals.quantity} unit={item.unitOfMeasure} strong className="text-base" />
                  <PackHint quantity={summary.totals.quantity} pack={pack} baseUnit={item.unitOfMeasure} />
                </span>
              </li>
            </ul>
          </Card>
        )}

        <Card className="p-5">
          <SectionTitle
            icon={Tags}
            end={<Button variant="ghost" size="sm" onClick={onOpenPrices}>{t('profile.overview.allPrices', { defaultValue: 'All prices' })}</Button>}
          >
            {t('profile.overview.prices', { defaultValue: 'Prices' })}
          </SectionTitle>
          <ul className="flex flex-col divide-y divide-border text-sm">
            <li className="flex items-center justify-between gap-3 py-2">
              <span className="font-medium">{t('profile.prices.normal', { defaultValue: 'Normal price' })}</span>
              <span className="font-mono font-semibold tabular-nums">{money(item.salePrice, item.saleCurrencyCode)}</span>
            </li>
            {baseGroupPrices.map((g) => (
              <li key={g.priceListId} className="flex items-center justify-between gap-3 py-2">
                <span className="truncate text-fg-muted">{g.priceListName}</span>
                <span className="font-mono tabular-nums">{money(g.unitPrice, g.currencyCode ?? g.listCurrencyCode)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-fg-subtle">{t('profile.overview.perUnit', { defaultValue: 'Per {{unit}}. Packs and every group on the Prices tab.', unit: item.unitOfMeasure })}</p>
        </Card>
      </div>
    </div>
  );
}
